import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {Component, Type} from '@angular/core';
import {Step} from '../application-flow/steps/step';
import {ResidenceProofStep} from '../application-flow/steps/residence-proof.step';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {AddressProofType, AddressProofTypeModel} from '../model/address-proof-type.model';
import {Validator} from '../utils/Validator';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {Logger} from '../utils/Logger';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {UserAddressInteractor} from '../interactors/user-address.interactor';
import {ActivatedRoute, Data} from '@angular/router';
import {isEmpty} from 'lodash';
import {Proof} from '../model/Proof';
import {UserInteractor} from '../interactors/user.interactor';

@Component({
    templateUrl: 'residence-proof.component.html',
    styleUrls: ['residence-proof.component.scss', '../loan-application-flow-component/application-flow.css']
})
export class ResidenceProofComponent extends AbstractApplicationFlowComponent {
    public readonly addressProofTypes = AddressProofTypeModel.getAllAddressProofType();
    public readonly AddressProofTypeModel = AddressProofTypeModel;
    public readonly AddressProofType = AddressProofType;
    public residenceProofFormGroup: FormGroup;
    public shouldShowLoader: boolean;
    public aadhaarLinkAnswers: any = [
        {value: true, displayName: 'Yes, it is linked'},
        {value: false, displayName: 'No, it is not linked'},
    ];
    public isFormSubmitted = false;
    private proofs: Proof[];
    private readonly ProofIdFormControl: FormControl;
    private userInteractor: UserInteractor;

    constructor(loanApplicationFlowNavigator: LoanApplicationFlowNavigator,
                formBuilder: FormBuilder,
                private userAddressInteractor: UserAddressInteractor,
                private loanApplicationInteractor: LoanApplicationInteractor,
                activatedRoute: ActivatedRoute, userInteractor: UserInteractor) {
        super(loanApplicationFlowNavigator);
        this.userInteractor = userInteractor;
        this.ProofIdFormControl = formBuilder.control(null,
            [Validators.required, Validator.notEmpty, Validators.maxLength(20),
                Validators.pattern(/^[a-zA-Z0-9-\/ ]+$/)]);
        this.residenceProofFormGroup = formBuilder.group({
            proofType: formBuilder.control(null, [Validators.required]),
            proofId: this.ProofIdFormControl,
            aadhaarLinkedWithMobile: formBuilder.control(null, [Validators.required])
        });

        activatedRoute.data.subscribe((data: Data) => {
            this.prePopulateResidenceProofInfo(data);
        });
    }

    step(): Type<Step> {
        return ResidenceProofStep;
    }

    public resetProofNumber(formGroup: FormGroup) {
        formGroup.controls.proofId.reset();
    }

    async onSubmit(formGroup: FormGroup): Promise<void> {
        this.isFormSubmitted = true;
        FormGroupUtil.validateFormGroup(formGroup);
        if (formGroup.invalid) {
            Logger.log(`Form is not valid. Not going ahead with the submission.`);
            return;
        }

        this.shouldShowLoader = true;
        const type = formGroup.controls.proofType.value;
        const number = formGroup.controls.proofId ? formGroup.controls.proofId.value : undefined;
        const proof = new Proof(type, number);

        try {
            const addressId = (await this.loanApplicationInteractor.get()).addressId;
            const address = await this.userAddressInteractor.get(addressId);
            if (isEmpty(address.proofs)) {
                address.proofs = [proof];
            } else {
                address.proofs[0].proofNumber = proof.proofNumber;
                address.proofs[0].proofType = proof.proofType;
            }
            await this.userAddressInteractor.update(address);
            await this.userInteractor.updateAadhaarLinkToMobileAnswer(formGroup.controls.aadhaarLinkedWithMobile.value);
            await this.nextPage();
        } finally {
            this.shouldShowLoader = false;
        }
    }

    public onProofTypeChanged(formGroup: FormGroup) {
        const proofIdFormControlName = 'proofId';
        if (formGroup.controls.proofType.value === AddressProofType.aadhaar) {
            this.resetProofNumber(formGroup);
            formGroup.removeControl(proofIdFormControlName);
        } else if (!formGroup.contains(proofIdFormControlName)) {
            formGroup.addControl(proofIdFormControlName, this.ProofIdFormControl);
            this.resetProofNumber(formGroup);
        }
    }

    protected prePopulateResidenceProofInfo(data: Data): void {
        if (!isEmpty(data.residenceInformation.address.proofs)) {
            this.proofs = data.residenceInformation.address.proofs;
            this.residenceProofFormGroup.patchValue({
                proofType: this.proofs[0].proofType,
                proofId: this.proofs[0].proofNumber,
                aadhaarLinkedWithMobile: data.residenceInformation.aadhaarLinkedWithMobile
            });
            this.onProofTypeChanged(this.residenceProofFormGroup);
        }
    }

    protected errorMessages(): Object {
        return {
            proofType: [{
                type: 'required',
                message: 'Please select a proof type'
            }],
            proofId: [{
                type: 'required',
                message: 'Please enter a proof number'
            }, {
                type: 'maxlength',
                message: 'Please reduce the length of the proof number'
            }, {
                type: 'pattern',
                message: 'Please enter a valid proof number'
            }, {
                type: 'empty',
                message: 'Please enter a proof number'
            }],
            aadhaarLinkedWithMobile: [{
                type: 'required',
                message: 'Please select one of the options'
            }]
        };
    }
}
